import { NavLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Home,
  Package,
  Users,
  FolderOpen,
  AppWindow,
  ClipboardList,
  Inbox,
  User,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/lib/api-client'
import { cn } from '@/lib/utils'

const employeeNavigation = [
  { name: 'My Assignments', href: '/', icon: ClipboardList },
  { name: 'Requests', href: '/requests', icon: Inbox },
  { name: 'My software', href: '/software', icon: AppWindow },
]

const adminNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Devices', href: '/devices', icon: Package },
  { name: 'Requests', href: '/requests', icon: Inbox },
  { name: 'Software', href: '/software', icon: AppWindow },
  { name: 'Projects', href: '/projects', icon: FolderOpen },
  { name: 'Users', href: '/users', icon: Users },
]

export function Sidebar() {
  const { user } = useAuth()
  const navigation = user?.role === 'Admin' ? adminNavigation : employeeNavigation
  const { data: openRequests = 0 } = useQuery({
    queryKey: ['device-requests', 'pending-count', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const response = await apiClient.get('/device-requests', { params: { status: 'pending' } })
      return (response.data || []).length
    },
  })

  return (
    <aside className="hidden h-full min-h-0 w-60 shrink-0 flex-col overflow-hidden border-r border-border bg-card lg:flex">
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Package className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-none">Simplogics</p>
              <p className="mt-1 truncate text-[11px] text-muted-foreground">Assets</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === '/'}
              className={({ isActive }) =>
                cn(
                  'flex h-9 items-center rounded-md px-2.5 text-sm transition-colors',
                  isActive
                    ? 'bg-primary/10 font-medium text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )
              }
            >
              <item.icon className="mr-2.5 h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              {item.href === '/requests' && openRequests > 0 && (
                <span className="ml-2 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
                  {openRequests}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="shrink-0 border-t border-border p-2">
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-md px-2 py-2 transition-colors',
                isActive ? 'bg-primary/10' : 'hover:bg-muted'
              )
            }
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
              <User className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium leading-none">{user?.name}</p>
              <p className="mt-1 truncate text-xs text-muted-foreground">{user?.role}</p>
            </div>
          </NavLink>
        </div>
    </aside>
  )
}
